import logoColor from '../assets/logo-color.svg';

const fmt = n => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 }).format(n || 0);
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '—';

const baseStyles = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: system-ui, -apple-system, sans-serif; color: #1f2937; background: white; font-size: 13px; line-height: 1.5; }
  .page { max-width: 800px; margin: 0 auto; padding: 48px; }
  table { width: 100%; border-collapse: collapse; }
  @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
`;

const header = (numero, typeLabel, dateEmission, dateExtra, extraLabel) => `
  <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:48px;">
    <div>
      <img src="${logoColor}" alt="LA DONNÉE" style="height:44px;object-fit:contain;display:block;margin-bottom:8px;" />
      <div style="font-size:12px;color:#9ca3af;margin-top:2px;line-height:1.7;">
        la-donnee.fr · contact@la-donnee.fr<br>Amiens, Hauts-de-France
      </div>
    </div>
    <div style="text-align:right;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;letter-spacing:0.12em;margin-bottom:4px;">${typeLabel}</div>
      <div style="font-size:22px;font-weight:800;color:#41B073;font-family:monospace;">${numero}</div>
      <div style="font-size:12px;color:#9ca3af;margin-top:6px;">Émis le ${fmtDate(dateEmission)}</div>
      ${dateExtra ? `<div style="font-size:12px;color:#9ca3af;">${extraLabel} ${fmtDate(dateExtra)}</div>` : ''}
    </div>
  </div>
`;

const clientBlock = (clientNom, contactPrenom, contactNom) => `
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-bottom:40px;padding:20px;background:#f9fafb;border-radius:10px;">
    <div>
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:8px;letter-spacing:0.08em;">Prestataire</div>
      <div style="font-weight:700;color:#1f2937;font-size:14px;">LA DONNÉE</div>
      <div style="color:#6b7280;margin-top:4px;font-size:12px;line-height:1.7;">
        contact@la-donnee.fr<br>Amiens, Hauts-de-France
      </div>
    </div>
    <div>
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:8px;letter-spacing:0.08em;">Client</div>
      <div style="font-weight:700;color:#1f2937;font-size:14px;">${clientNom || '—'}</div>
      ${contactPrenom ? `<div style="color:#6b7280;margin-top:4px;font-size:12px;">${contactPrenom} ${contactNom || ''}</div>` : ''}
    </div>
  </div>
`;

const totalsBlock = (montantHt, tauxTva) => {
  const tva = montantHt * (tauxTva / 100);
  const ttc = montantHt + tva;
  return `
    <div style="display:flex;justify-content:flex-end;margin-bottom:40px;">
      <div style="width:300px;background:#f9fafb;border-radius:10px;padding:18px;">
        <div style="display:flex;justify-content:space-between;margin-bottom:8px;font-size:13px;">
          <span style="color:#6b7280;">Total HT</span>
          <span style="font-family:monospace;color:#374151;">${fmt(montantHt)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;margin-bottom:14px;font-size:13px;">
          <span style="color:#6b7280;">TVA (${tauxTva}%)</span>
          <span style="font-family:monospace;color:#374151;">${fmt(tva)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;padding-top:14px;border-top:2px solid #e5e7eb;font-size:16px;font-weight:800;">
          <span style="color:#1F305A;">Total TTC</span>
          <span style="font-family:monospace;color:#41B073;">${fmt(ttc)}</span>
        </div>
      </div>
    </div>
  `;
};

const linesTable = (lignes) => {
  const rows = (lignes || []).map(l => `
    <tr>
      <td style="padding:10px 10px;border-bottom:1px solid #f3f4f6;">
        <div style="font-weight:500;color:#1f2937;">${l.description || ''}</div>
        ${l.note ? `<div style="font-size:11px;color:#9ca3af;margin-top:3px;">${l.note}</div>` : ''}
      </td>
      <td style="padding:10px 10px;border-bottom:1px solid #f3f4f6;text-align:center;color:#9ca3af;font-size:12px;">${l.unite || '—'}</td>
      <td style="padding:10px 10px;border-bottom:1px solid #f3f4f6;text-align:right;color:#6b7280;">${l.quantite}</td>
      <td style="padding:10px 10px;border-bottom:1px solid #f3f4f6;text-align:right;font-family:monospace;color:#6b7280;">${fmt(l.prix_unitaire)}</td>
      <td style="padding:10px 10px;border-bottom:1px solid #f3f4f6;text-align:right;font-family:monospace;font-weight:700;color:#41B073;">${fmt((l.quantite || 0) * (l.prix_unitaire || 0))}</td>
    </tr>
  `).join('');

  return `
    <table style="margin-bottom:8px;">
      <thead>
        <tr style="background:#1F305A;">
          <th style="padding:11px 10px;text-align:left;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:white;border-radius:8px 0 0 0;">Description</th>
          <th style="padding:11px 10px;text-align:center;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:white;">Unité</th>
          <th style="padding:11px 10px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:white;">Qté</th>
          <th style="padding:11px 10px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:white;">PU HT</th>
          <th style="padding:11px 10px;text-align:right;font-size:10px;text-transform:uppercase;letter-spacing:0.08em;color:white;border-radius:0 8px 0 0;">Total HT</th>
        </tr>
      </thead>
      <tbody>${rows}</tbody>
    </table>
  `;
};

const docFooter = () => `
  <div style="margin-top:60px;padding-top:20px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;color:#9ca3af;font-size:11px;">
    <div>LA DONNÉE · Fabriquons ensemble.</div>
    <div>la-donnee.fr · contact@la-donnee.fr</div>
    <div>Amiens, Hauts-de-France</div>
  </div>
`;

function openPrintWindow(title, bodyHtml) {
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) { alert("Veuillez autoriser les popups pour générer le PDF."); return; }
  win.document.write(`<!DOCTYPE html><html lang="fr"><head><meta charset="UTF-8"><title>${title}</title><style>${baseStyles}</style></head><body><div class="page">${bodyHtml}</div></body></html>`);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); }, 600);
}

export function printDevis(devis) {
  const body = `
    ${header(devis.numero, 'Devis', devis.date_emission, devis.date_validite, "Valide jusqu'au")}
    ${clientBlock(devis.client_nom, devis.contact_prenom, devis.contact_nom)}
    <div style="margin-bottom:24px;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:6px;letter-spacing:0.08em;">Objet</div>
      <div style="font-size:17px;font-weight:700;color:#1f2937;">${devis.titre || ''}</div>
    </div>
    ${linesTable(devis.lignes)}
    ${totalsBlock(devis.montant_ht, devis.taux_tva)}
    ${devis.conditions ? `<div style="border-top:1px solid #e5e7eb;padding-top:24px;margin-bottom:24px;"><div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:8px;letter-spacing:0.08em;">Conditions générales</div><div style="font-size:12px;color:#6b7280;white-space:pre-wrap;line-height:1.7;">${devis.conditions}</div></div>` : ''}
    ${devis.notes ? `<div style="border-top:1px solid #e5e7eb;padding-top:20px;margin-bottom:16px;"><div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:6px;letter-spacing:0.08em;">Notes</div><div style="font-size:12px;color:#6b7280;">${devis.notes}</div></div>` : ''}
    <div style="margin-top:40px;padding-top:20px;border-top:1px solid #e5e7eb;">
      <div style="font-size:12px;color:#9ca3af;text-align:center;font-style:italic;">Bon pour accord — Signature et cachet :</div>
      <div style="margin-top:40px;border-bottom:1px solid #9ca3af;width:200px;margin-left:auto;"></div>
      <div style="font-size:11px;color:#9ca3af;text-align:right;margin-top:6px;">Date et signature</div>
    </div>
    ${docFooter()}
  `;
  openPrintWindow(`${devis.numero} — ${devis.titre}`, body);
}

// ─── DPA — Accord de traitement des données ───────────────────────────────────
export function printDPA(mission, client) {
  const today = new Date().toLocaleDateString('fr-FR');
  const body = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:48px;">
      <div>
        <img src="${logoColor}" alt="LA DONNÉE" style="height:44px;object-fit:contain;display:block;margin-bottom:8px;" />
        <div style="font-size:12px;color:#9ca3af;margin-top:2px;line-height:1.7;">la-donnee.fr · contact@la-donnee.fr<br>Amiens, Hauts-de-France</div>
      </div>
      <div style="text-align:right;">
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;letter-spacing:0.12em;margin-bottom:4px;">Accord de traitement des données</div>
        <div style="font-size:13px;color:#41B073;margin-top:4px;">DPA — Data Processing Agreement</div>
        <div style="font-size:12px;color:#9ca3af;margin-top:6px;">Émis le ${today}</div>
      </div>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:32px;margin-bottom:40px;padding:20px;background:#f9fafb;border-radius:10px;">
      <div>
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:8px;letter-spacing:0.08em;">Responsable du traitement (Client)</div>
        <div style="font-weight:700;color:#1f2937;font-size:14px;">${client?.nom || '—'}</div>
        <div style="color:#6b7280;margin-top:4px;font-size:12px;line-height:1.7;">${client?.secteur || ''}<br>${client?.ville || ''}<br>${client?.email || ''}</div>
      </div>
      <div>
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:8px;letter-spacing:0.08em;">Sous-traitant (Prestataire)</div>
        <div style="font-weight:700;color:#1f2937;font-size:14px;">LA DONNÉE</div>
        <div style="color:#6b7280;margin-top:4px;font-size:12px;line-height:1.7;">contact@la-donnee.fr<br>Amiens, Hauts-de-France</div>
      </div>
    </div>

    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
      <div style="font-size:13px;font-weight:700;color:#1F305A;margin-bottom:12px;">1. Objet de la mission</div>
      <div style="font-size:13px;color:#374151;line-height:1.7;"><strong>${mission?.titre || '—'}</strong></div>
      ${mission?.description ? `<div style="font-size:12px;color:#6b7280;margin-top:8px;line-height:1.7;">${mission.description}</div>` : ''}
      <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-top:16px;">
        <div style="background:#f9fafb;border-radius:8px;padding:10px;">
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Début</div>
          <div style="font-size:12px;font-weight:600;color:#374151;">${fmtDate(mission?.date_debut)}</div>
        </div>
        <div style="background:#f9fafb;border-radius:8px;padding:10px;">
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Fin prévue</div>
          <div style="font-size:12px;font-weight:600;color:#374151;">${fmtDate(mission?.date_fin)}</div>
        </div>
        <div style="background:#fef3c7;border-radius:8px;padding:10px;border:1px solid #fde68a;">
          <div style="font-size:10px;color:#92400e;text-transform:uppercase;margin-bottom:4px;">Suppression données</div>
          <div style="font-size:12px;font-weight:700;color:#b45309;">${fmtDate(mission?.rgpd_date_suppression)}</div>
        </div>
      </div>
    </div>

    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
      <div style="font-size:13px;font-weight:700;color:#1F305A;margin-bottom:12px;">2. Nature des données traitées</div>
      ${(mission?.rgpd_sources || []).length > 0 ? `
        <table style="width:100%;border-collapse:collapse;font-size:12px;">
          <thead>
            <tr style="background:#f3f4f6;">
              <th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:600;text-transform:uppercase;font-size:10px;">Source</th>
              <th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:600;text-transform:uppercase;font-size:10px;">Type</th>
              <th style="padding:8px 10px;text-align:left;color:#6b7280;font-weight:600;text-transform:uppercase;font-size:10px;">Données personnelles</th>
            </tr>
          </thead>
          <tbody>
            ${(mission.rgpd_sources || []).map(s => `
              <tr style="border-bottom:1px solid #f3f4f6;">
                <td style="padding:8px 10px;color:#374151;">${s.source || '—'}</td>
                <td style="padding:8px 10px;color:#6b7280;">${s.type || '—'}</td>
                <td style="padding:8px 10px;color:${s.donnees_perso ? '#d97706' : '#059669'};font-weight:600;">${s.donnees_perso ? '⚠️ Oui' : '✓ Non'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      ` : '<div style="font-size:12px;color:#9ca3af;">Non renseignées</div>'}
    </div>

    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
      <div style="font-size:13px;font-weight:700;color:#1F305A;margin-bottom:12px;">3. Engagements du sous-traitant (LA DONNÉE)</div>
      <ul style="font-size:12px;color:#374151;line-height:2;padding-left:18px;">
        <li>Traiter les données uniquement aux fins définies dans cet accord et sur instruction documentée du responsable du traitement</li>
        <li>Assurer la confidentialité des données (accès restreint aux personnes habilitées)</li>
        <li>Mettre en œuvre les mesures de sécurité appropriées : chiffrement, accès sécurisé, hébergement en France</li>
        <li>Ne pas transférer les données hors Union Européenne sans autorisation explicite</li>
        <li>Notifier toute violation de données dans les 72h (article 33 RGPD)</li>
        <li>Supprimer ou restituer toutes les données au terme de la mission (au plus tard le ${fmtDate(mission?.rgpd_date_suppression)})</li>
        <li>Coopérer avec les audits du responsable de traitement ou de la CNIL</li>
      </ul>
    </div>

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:50px;padding-top:20px;border-top:1px solid #e5e7eb;">
      <div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:32px;">Pour le Responsable du traitement :<br><strong style="color:#374151;">${client?.nom || '—'}</strong></div>
        <div style="border-bottom:1px solid #9ca3af;margin-bottom:6px;"></div>
        <div style="font-size:11px;color:#9ca3af;">Date et signature</div>
      </div>
      <div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:32px;">Pour le Sous-traitant :<br><strong style="color:#374151;">LA DONNÉE</strong></div>
        <div style="border-bottom:1px solid #9ca3af;margin-bottom:6px;"></div>
        <div style="font-size:11px;color:#9ca3af;">Date et signature</div>
      </div>
    </div>
    ${docFooter()}
  `;
  openPrintWindow(`DPA — ${client?.nom || 'Client'} — ${mission?.titre || 'Mission'}`, body);
}

// ─── Attestation de suppression des données ───────────────────────────────────
export function printAttestation(mission, client) {
  const today = new Date().toLocaleDateString('fr-FR');
  const body = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:48px;">
      <div>
        <img src="${logoColor}" alt="LA DONNÉE" style="height:44px;object-fit:contain;display:block;" />
      </div>
      <div style="text-align:right;">
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;letter-spacing:0.12em;margin-bottom:4px;">Attestation de suppression</div>
        <div style="font-size:12px;color:#9ca3af;margin-top:6px;">Émise le ${today}</div>
      </div>
    </div>

    <div style="background:#f0fdf4;border:2px solid #86efac;border-radius:12px;padding:24px;margin-bottom:32px;text-align:center;">
      <div style="font-size:32px;margin-bottom:8px;">✅</div>
      <div style="font-size:18px;font-weight:700;color:#166534;">Attestation de suppression des données</div>
      <div style="font-size:13px;color:#16a34a;margin-top:6px;">Mission clôturée — données supprimées conformément au RGPD</div>
    </div>

    <div style="font-size:13px;color:#374151;line-height:2;margin-bottom:32px;">
      La société <strong>LA DONNÉE</strong>, sous-traitant au sens du RGPD (Règlement UE 2016/679), atteste par le présent document avoir procédé à la <strong>suppression définitive et irréversible</strong> de l'ensemble des données traitées dans le cadre de la mission décrite ci-dessous.
    </div>

    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
      <div style="display:grid;grid-template-columns:1fr 1fr;gap:20px;">
        <div>
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Client (responsable du traitement)</div>
          <div style="font-size:14px;font-weight:700;color:#1f2937;">${client?.nom || '—'}</div>
          <div style="font-size:12px;color:#6b7280;margin-top:2px;">${client?.secteur || ''} · ${client?.ville || ''}</div>
        </div>
        <div>
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Mission</div>
          <div style="font-size:14px;font-weight:700;color:#1f2937;">${mission?.titre || '—'}</div>
        </div>
        <div>
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Période de traitement</div>
          <div style="font-size:13px;color:#374151;">${fmtDate(mission?.date_debut)} → ${fmtDate(mission?.date_fin)}</div>
        </div>
        <div>
          <div style="font-size:10px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px;">Date de suppression effective</div>
          <div style="font-size:13px;font-weight:700;color:#166534;">${fmtDate(mission?.rgpd_date_suppression_effective) || today}</div>
        </div>
      </div>
    </div>

    ${(mission?.rgpd_sources || []).length > 0 ? `
    <div style="background:#fff;border:1px solid #e5e7eb;border-radius:10px;padding:20px;margin-bottom:24px;">
      <div style="font-size:12px;font-weight:700;color:#1F305A;margin-bottom:12px;">Sources de données supprimées</div>
      <table style="width:100%;border-collapse:collapse;font-size:12px;">
        <thead><tr style="background:#f3f4f6;">
          <th style="padding:7px 10px;text-align:left;color:#6b7280;font-size:10px;text-transform:uppercase;">Source</th>
          <th style="padding:7px 10px;text-align:left;color:#6b7280;font-size:10px;text-transform:uppercase;">Type</th>
          <th style="padding:7px 10px;text-align:left;color:#6b7280;font-size:10px;text-transform:uppercase;">Données perso</th>
          <th style="padding:7px 10px;text-align:center;color:#6b7280;font-size:10px;text-transform:uppercase;">Statut</th>
        </tr></thead>
        <tbody>${(mission.rgpd_sources || []).map(s => `
          <tr style="border-bottom:1px solid #f3f4f6;">
            <td style="padding:7px 10px;color:#374151;">${s.source || '—'}</td>
            <td style="padding:7px 10px;color:#6b7280;">${s.type || '—'}</td>
            <td style="padding:7px 10px;color:${s.donnees_perso ? '#d97706' : '#6b7280'};">${s.donnees_perso ? 'Oui' : 'Non'}</td>
            <td style="padding:7px 10px;text-align:center;color:#166534;font-weight:700;">✓ Supprimé</td>
          </tr>`).join('')}
        </tbody>
      </table>
    </div>` : ''}

    <div style="display:grid;grid-template-columns:1fr 1fr;gap:40px;margin-top:50px;padding-top:20px;border-top:1px solid #e5e7eb;">
      <div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:32px;">Reçu pour acquit :<br><strong style="color:#374151;">${client?.nom || '—'}</strong></div>
        <div style="border-bottom:1px solid #9ca3af;margin-bottom:6px;"></div>
        <div style="font-size:11px;color:#9ca3af;">Date et signature</div>
      </div>
      <div>
        <div style="font-size:11px;color:#9ca3af;margin-bottom:32px;">Attestation émise par :<br><strong style="color:#374151;">LA DONNÉE</strong></div>
        <div style="border-bottom:1px solid #9ca3af;margin-bottom:6px;"></div>
        <div style="font-size:11px;color:#9ca3af;">Date et signature</div>
      </div>
    </div>
    ${docFooter()}
  `;
  openPrintWindow(`Attestation suppression — ${client?.nom || 'Client'}`, body);
}

// ─── Registre des traitements CNIL ────────────────────────────────────────────
export function printRegistre(registreData) {
  const today = new Date().toLocaleDateString('fr-FR');
  const rows  = (registreData?.data || []).map(r => `
    <tr style="border-bottom:1px solid #f3f4f6;font-size:11px;">
      <td style="padding:8px 10px;font-weight:600;color:#1f2937;">${r.client_nom}</td>
      <td style="padding:8px 10px;color:#6b7280;">${r.client_secteur}</td>
      <td style="padding:8px 10px;color:#374151;">${r.mission_titre}</td>
      <td style="padding:8px 10px;color:#6b7280;">${r.base_legale}</td>
      <td style="padding:8px 10px;color:#6b7280;">${r.types_donnees}</td>
      <td style="padding:8px 10px;color:#6b7280;">${r.duree_traitement}</td>
      <td style="padding:8px 10px;color:#6b7280;">${r.mesures_securite}</td>
      <td style="padding:8px 10px;color:${r.dpa_signe === 'Oui' ? '#059669' : '#dc2626'};font-weight:600;">${r.dpa_signe}</td>
      <td style="padding:8px 10px;color:${r.rgpd_statut.includes('supprimé') || r.rgpd_statut.includes('Supprimées') ? '#059669' : '#d97706'};">${r.rgpd_statut}</td>
    </tr>
  `).join('');

  const body = `
    <div style="display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:40px;">
      <div>
        <img src="${logoColor}" alt="LA DONNÉE" style="height:44px;object-fit:contain;display:block;" />
      </div>
      <div style="text-align:right;">
        <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;letter-spacing:0.12em;margin-bottom:4px;">Registre des activités de traitement</div>
        <div style="font-size:11px;color:#41B073;margin-top:2px;">Article 30 RGPD (UE 2016/679)</div>
        <div style="font-size:11px;color:#9ca3af;margin-top:4px;">Généré le ${today} · ${registreData?.total || 0} traitement(s)</div>
      </div>
    </div>

    <div style="background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:12px 16px;margin-bottom:24px;font-size:11px;color:#92400e;line-height:1.7;">
      <strong>Responsable du traitement :</strong> LA DONNÉE — Amiens, Hauts-de-France — contact@la-donnee.fr<br>
      <strong>Délégué à la protection des données :</strong> contact@la-donnee.fr<br>
      <strong>Finalité générale :</strong> Prestation de services data (tableaux de bord, analyses, applications métier) pour le compte de clients professionnels.
    </div>

    <table style="width:100%;border-collapse:collapse;font-size:11px;">
      <thead>
        <tr style="background:#1F305A;">
          ${['Client','Secteur','Mission','Base légale','Types données','Durée','Mesures sécurité','DPA','Statut'].map(h => `
            <th style="padding:9px 10px;text-align:left;color:white;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;font-weight:700;">${h}</th>
          `).join('')}
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="9" style="padding:20px;text-align:center;color:#9ca3af;">Aucun traitement enregistré</td></tr>'}</tbody>
    </table>

    <div style="margin-top:40px;font-size:10px;color:#9ca3af;line-height:1.8;border-top:1px solid #e5e7eb;padding-top:16px;">
      Document généré conformément à l'article 30 du Règlement (UE) 2016/679 du Parlement Européen et du Conseil (RGPD).<br>
      Ce registre doit être mis à jour régulièrement et mis à disposition de la CNIL sur demande.
    </div>
    ${docFooter()}
  `;
  openPrintWindow('Registre des traitements CNIL', body);
}

export function printFacture(facture) {
  const body = `
    ${header(facture.numero, 'Facture', facture.date_emission, facture.date_echeance, 'Échéance le')}
    ${clientBlock(facture.client_nom, facture.contact_prenom, facture.contact_nom)}
    <div style="margin-bottom:24px;">
      <div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:6px;letter-spacing:0.08em;">Objet</div>
      <div style="font-size:17px;font-weight:700;color:#1f2937;">${facture.titre || ''}</div>
    </div>
    ${facture.lignes && facture.lignes.length > 0 ? linesTable(facture.lignes) : ''}
    ${totalsBlock(facture.montant_ht, facture.taux_tva)}
    ${facture.statut === 'Payée' && facture.date_paiement ? `<div style="background:#f0fdf4;border:1px solid #86efac;border-radius:8px;padding:12px 16px;margin-bottom:24px;font-size:13px;color:#166534;">✓ Payée le ${fmtDate(facture.date_paiement)}${facture.mode_paiement ? ` · ${facture.mode_paiement}` : ''}</div>` : ''}
    <div style="border-top:1px solid #e5e7eb;padding-top:20px;margin-bottom:16px;">
      <div style="font-size:12px;color:#6b7280;line-height:1.8;">
        Règlement par virement bancaire à réception de facture.<br>
        En cas de retard de paiement, des pénalités de 3× le taux d'intérêt légal seront appliquées.<br>
        Pas d'escompte pour règlement anticipé.
      </div>
    </div>
    ${facture.notes ? `<div style="border-top:1px solid #e5e7eb;padding-top:16px;margin-bottom:16px;"><div style="font-size:10px;font-weight:700;text-transform:uppercase;color:#9ca3af;margin-bottom:6px;letter-spacing:0.08em;">Notes</div><div style="font-size:12px;color:#6b7280;">${facture.notes}</div></div>` : ''}
    ${docFooter()}
  `;
  openPrintWindow(`${facture.numero} — ${facture.titre}`, body);
}
